/**
 * The Social presence form: turns the venue's `social` into form text, and the
 * form text back into the smallest marketing patch that says what changed.
 */
import type { PlatformPatch, VenueMarketingPatch } from '../../../../api/venue-marketing';
import {
  SOCIAL_PLATFORMS,
  type PostingCadence,
  type SocialPlatform,
  type VenueSocial,
} from '../../../../api/venues';

import { normalizeHandle, withHttpsScheme } from './social-format';

export interface PlatformDraft {
  handle: string;
  /** Digits as typed. Blank means unknown. */
  audience: string;
  /** "Unknown" is saved as null, never as a value. */
  cadence: PostingCadence;
}

export type SocialDraft = Record<SocialPlatform, PlatformDraft> & { website: string };

type SocialPatch = NonNullable<VenueMarketingPatch['social']>;

export const PLATFORM_LABELS: Record<SocialPlatform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
};

export const CADENCE_CHOICES: ReadonlyArray<{ value: PostingCadence; label: string }> = [
  { value: 'UNKNOWN', label: 'Unknown' },
  { value: 'WEEKLY_PLUS', label: 'Weekly or more' },
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'DORMANT', label: 'Dormant' },
];

const AUDIENCE_ERROR = 'Enter a whole number, or leave blank for unknown.';

export function cadenceLabel(cadence: PostingCadence | undefined): string {
  return CADENCE_CHOICES.find((c) => c.value === cadence)?.label ?? 'Unknown';
}

/** "950", "1.5K", "28K". Null when the size is unknown, so it is never shown as 0. */
export function formatAudience(n: number | undefined): string | null {
  if (n === undefined) return null;
  if (n < 1000) return String(n);
  const digits = n % 1000 === 0 || n >= 10000 ? 0 : 1;
  return `${(n / 1000).toFixed(digits)}K`;
}

/** The key the server uses for an error on this field, e.g. `social.instagram.handle`. */
export function socialErrorKey(
  target: SocialPlatform | 'website',
  field?: 'handle' | 'audienceSize' | 'cadence',
): string {
  return field ? `social.${target}.${field}` : `social.${target}`;
}

function seedPlatform(presence: VenueSocial[SocialPlatform]): PlatformDraft {
  return {
    handle: presence?.handle ?? '',
    audience: presence?.audienceSize === undefined ? '' : String(presence.audienceSize),
    cadence: presence?.cadence ?? 'UNKNOWN',
  };
}

export function seedSocialDraft(social: VenueSocial | null): SocialDraft {
  return {
    instagram: seedPlatform(social?.instagram),
    facebook: seedPlatform(social?.facebook),
    tiktok: seedPlatform(social?.tiktok),
    website: social?.website ?? '',
  };
}

/** A whole number from the typed text, null when blank, or undefined when invalid. */
function parseAudience(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  return /^\d[\d,]*$/.test(trimmed) ? Number(trimmed.replace(/,/g, '')) : undefined;
}

const sameAs = (a: PlatformDraft, b: PlatformDraft) =>
  normalizeHandle(a.handle) === normalizeHandle(b.handle) &&
  a.audience.trim() === b.audience.trim() &&
  a.cadence === b.cadence;

const NEEDS_HANDLE = 'Enter a handle to save followers or cadence';

/**
 * A platform with no handle is cleared, as long as its audience and cadence are
 * left as they were. Audience or cadence typed without a handle is an error:
 * it is never dropped silently.
 */
function addPlatformWithoutHandle(
  platform: SocialPlatform,
  draft: PlatformDraft,
  original: PlatformDraft,
  out: { patch: SocialPatch; errors: Record<string, string> },
): void {
  const hasExtras = draft.audience.trim() !== '' || draft.cadence !== 'UNKNOWN';
  const extrasChanged =
    draft.audience.trim() !== original.audience.trim() || draft.cadence !== original.cadence;
  if (hasExtras && extrasChanged) {
    out.errors[socialErrorKey(platform, 'handle')] = NEEDS_HANDLE;
    return;
  }
  if (original.handle !== '') out.patch[platform] = null;
}

/** Adds the change for one platform, or an error, to the patch being built. */
function addPlatform(
  platform: SocialPlatform,
  draft: PlatformDraft,
  original: PlatformDraft,
  out: { patch: SocialPatch; errors: Record<string, string> },
): void {
  if (sameAs(draft, original)) return;
  const handle = normalizeHandle(draft.handle);
  if (handle === '') {
    addPlatformWithoutHandle(platform, draft, original, out);
    return;
  }
  const audienceSize = parseAudience(draft.audience);
  if (audienceSize === undefined) {
    out.errors[socialErrorKey(platform, 'audienceSize')] = AUDIENCE_ERROR;
    return;
  }
  const next: PlatformPatch = {
    handle,
    audienceSize,
    cadence: draft.cadence === 'UNKNOWN' ? null : draft.cadence,
  };
  out.patch[platform] = next;
}

function addWebsite(draft: string, original: string, out: { patch: SocialPatch }): void {
  const typed = draft.trim();
  if (typed === original.trim()) return;
  if (typed === '') {
    out.patch.website = null;
    return;
  }
  out.patch.website = withHttpsScheme(typed);
}

/**
 * Compares the form with what the venue has and returns only the differences.
 * Untouched platforms are left out so the server keeps them as they are.
 */
export function buildSocialPatch(
  draft: SocialDraft,
  social: VenueSocial | null,
): { patch: SocialPatch; errors: Record<string, string> } {
  const original = seedSocialDraft(social);
  const out = { patch: {} as SocialPatch, errors: {} as Record<string, string> };
  for (const platform of SOCIAL_PLATFORMS) {
    addPlatform(platform, draft[platform], original[platform], out);
  }
  addWebsite(draft.website, original.website, out);
  return out;
}
